"use client";

import {
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  SELECT,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { FreeBodyDiagram } from "@/components/tools/physics-mechanics/free-body-diagram";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  type ForceVector,
  netForce,
} from "@/lib/tool-kit/physics-mechanics/force";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  ACCELERATION,
  ANGLE,
  FORCE,
  fromBase,
  MASS,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

export interface ForceRow {
  id: number;
  magnitude: string;
  unit: string;
  angle: string;
  angleUnit: string;
}

export interface NetStrings {
  forceLabel: string;
  forceMagnitude: string;
  forceAngle: string;
  addForce: string;
  removeForce: string;
  unitFor: string;
  massOptional: string;
  massHint: string;
  netForce: string;
  netDirection: string;
  sumX: string;
  sumY: string;
  acceleration: string;
  balanced: string;
  workingHeading: string;
  stepComponent: string;
  stepSum: string;
  stepMagnitude: string;
  stepDirection: string;
  stepAcceleration: string;
  tableHeading: string;
  columnForce: string;
  columnSize: string;
  columnAngle: string;
  columnFx: string;
  columnFy: string;
  diagramHeading: string;
  diagramTitle: string;
  diagramNet: string;
  diagramNote: string;
  netUnreadable: string;
  netNeedForce: string;
  netNegative: string;
  massNotPositive: string;
}

const MAX_FORCES = 6;
const RAD_TO_DEG = 180 / Math.PI;

export interface NetState {
  rows: ForceRow[];
  mass: string;
  massUnit: string;
}

interface ReadOk {
  forces: ForceVector[];
  mass: number | null;
}

function readNet(
  state: NetState
): ReadOk | { error: "unreadable" | "negative" | "need" | "mass"; n?: number } {
  const forces: ForceVector[] = [];
  for (const [index, row] of state.rows.entries()) {
    if (row.magnitude.trim() === "" && row.angle.trim() === "") {
      continue;
    }
    const magnitude = parseQuantity(row.magnitude);
    const angle = parseQuantity(row.angle);
    if (magnitude === null || angle === null) {
      return { error: "unreadable", n: index + 1 };
    }
    if (magnitude < 0) {
      return { error: "negative", n: index + 1 };
    }
    forces.push({
      magnitude: toBase(magnitude, FORCE, row.unit),
      angle: toBase(angle, ANGLE, row.angleUnit),
    });
  }
  if (forces.length === 0) {
    return { error: "need" };
  }
  if (state.mass.trim() === "") {
    return { forces, mass: null };
  }
  const mass = parseQuantity(state.mass);
  if (mass === null || !(mass > 0)) {
    return { error: "mass" };
  }
  return { forces, mass: toBase(mass, MASS, state.massUnit) };
}

function ForceRowEditor({
  row,
  index,
  strings,
  canRemove,
  onChange,
  onRemove,
}: {
  row: ForceRow;
  index: number;
  strings: NetStrings;
  canRemove: boolean;
  onChange: (patch: Partial<ForceRow>) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const base = `fc-net-${row.id}`;
  const unitClass = `${SELECT.replace("w-full", "")} w-24 shrink-0`;
  return (
    <fieldset className="grid gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-2">
      <legend className={`${HINT} px-1`}>{t(strings.forceLabel, { n })}</legend>
      <div className="space-y-1">
        <span aria-hidden="true" className={HINT}>
          {strings.columnSize}
        </span>
        <div className="flex gap-2">
          <input
            aria-label={t(strings.forceMagnitude, { n })}
            className={`${INPUT_MONO} min-w-0 flex-1`}
            id={`${base}-size`}
            inputMode="decimal"
            onChange={(event) => onChange({ magnitude: event.target.value })}
            value={row.magnitude}
          />
          <select
            aria-label={t(strings.unitFor, {
              name: t(strings.forceMagnitude, { n }),
            })}
            className={unitClass}
            onChange={(event) => onChange({ unit: event.target.value })}
            value={row.unit}
          >
            {unitOptions(FORCE).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <span aria-hidden="true" className={HINT}>
          {strings.columnAngle}
        </span>
        <div className="flex gap-2">
          <input
            aria-label={t(strings.forceAngle, { n })}
            className={`${INPUT_MONO} min-w-0 flex-1`}
            id={`${base}-angle`}
            inputMode="decimal"
            onChange={(event) => onChange({ angle: event.target.value })}
            value={row.angle}
          />
          <select
            aria-label={t(strings.unitFor, {
              name: t(strings.forceAngle, { n }),
            })}
            className={unitClass}
            onChange={(event) => onChange({ angleUnit: event.target.value })}
            value={row.angleUnit}
          >
            {unitOptions(ANGLE).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {canRemove ? (
        <div className="sm:col-span-2">
          <button className={BUTTON_SMALL} onClick={onRemove} type="button">
            {t(strings.removeForce, { n })}
          </button>
        </div>
      ) : null}
    </fieldset>
  );
}

export function NetForceInputs({
  state,
  onState,
  strings,
  nextId,
}: {
  state: NetState;
  onState: (state: NetState) => void;
  strings: NetStrings;
  nextId: () => number;
}) {
  const patch = (id: number, change: Partial<ForceRow>) =>
    onState({
      ...state,
      rows: state.rows.map((row) =>
        row.id === id ? { ...row, ...change } : row
      ),
    });
  return (
    <div className="space-y-4">
      {state.rows.map((row, index) => (
        <ForceRowEditor
          canRemove={state.rows.length > 1}
          index={index}
          key={row.id}
          onChange={(change) => patch(row.id, change)}
          onRemove={() =>
            onState({
              ...state,
              rows: state.rows.filter((other) => other.id !== row.id),
            })
          }
          row={row}
          strings={strings}
        />
      ))}
      {state.rows.length < MAX_FORCES ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            onState({
              ...state,
              rows: [
                ...state.rows,
                {
                  id: nextId(),
                  magnitude: "",
                  unit: state.rows.at(-1)?.unit ?? "N",
                  angle: "",
                  angleUnit: state.rows.at(-1)?.angleUnit ?? "deg",
                },
              ],
            })
          }
          type="button"
        >
          {strings.addForce}
        </button>
      ) : null}
      <div className="max-w-md">
        <QuantityField
          hint={strings.massHint}
          id="fc-net-mass"
          label={strings.massOptional}
          onUnit={(massUnit) => onState({ ...state, massUnit })}
          onValue={(mass) => onState({ ...state, mass })}
          unit={state.massUnit}
          unitLabel={t(strings.unitFor, { name: strings.massOptional })}
          units={unitOptions(MASS)}
          value={state.mass}
        />
      </div>
    </div>
  );
}

function netSteps(
  forces: readonly ForceVector[],
  net: ReturnType<typeof netForce>,
  strings: NetStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const steps = forces.map((force, index) =>
    t(strings.stepComponent, {
      n: index + 1,
      f: p(force.magnitude),
      angle: p(force.angle * RAD_TO_DEG),
      fx: r(net.components[index].fx),
      fy: r(net.components[index].fy),
    })
  );
  steps.push(t(strings.stepSum, { fx: r(net.fx), fy: r(net.fy) }));
  if (!net.balanced) {
    steps.push(
      t(strings.stepMagnitude, {
        fx: r(net.fx),
        fy: r(net.fy),
        magnitude: r(net.magnitude),
      })
    );
    steps.push(
      t(strings.stepDirection, {
        fx: r(net.fx),
        fy: r(net.fy),
        angle: r((net.angle ?? 0) * RAD_TO_DEG),
      })
    );
  }
  return steps;
}

export function NetForceResults({
  state,
  strings,
  fmt,
  sig,
}: {
  state: NetState;
  strings: NetStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const read = readNet(state);
  if ("error" in read) {
    if (read.error === "need") {
      return <Notice>{strings.netNeedForce}</Notice>;
    }
    if (read.error === "mass") {
      return <ErrorNotice>{strings.massNotPositive}</ErrorNotice>;
    }
    const message =
      read.error === "negative" ? strings.netNegative : strings.netUnreadable;
    return <ErrorNotice>{t(message, { n: read.n ?? 1 })}</ErrorNotice>;
  }
  const { forces, mass } = read;
  const net = netForce(forces);
  const unit = state.rows[0]?.unit ?? "N";
  const show = (newtons: number) =>
    withUnit(
      formatSig(fmt, fromBase(newtons, FORCE, unit), sig),
      unitSymbol(FORCE, unit)
    );
  const accel = mass === null ? null : net.magnitude / mass;
  const degrees = (net.angle ?? 0) * RAD_TO_DEG;
  return (
    <>
      {net.balanced ? <Notice>{strings.balanced}</Notice> : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.netForce}
          value={show(net.magnitude)}
        />
        {net.angle === null ? null : (
          <StatCard
            label={strings.netDirection}
            value={withUnit(formatSig(fmt, degrees, sig), "°")}
          />
        )}
        <StatCard label={strings.sumX} value={show(net.fx)} />
        <StatCard label={strings.sumY} value={show(net.fy)} />
        {accel === null ? null : (
          <StatCard
            emphasis
            label={strings.acceleration}
            value={withUnit(
              formatSig(fmt, fromBase(accel, ACCELERATION, "mps2"), sig),
              unitSymbol(ACCELERATION, "mps2")
            )}
          />
        )}
      </StatGrid>
      <Working
        heading={strings.workingHeading}
        steps={[
          ...netSteps(forces, net, strings, fmt, sig),
          ...(accel === null
            ? []
            : [
                t(strings.stepAcceleration, {
                  force: formatSig(fmt, net.magnitude, sig),
                  mass: formatPlain(fmt, mass ?? 0),
                  result: formatSig(fmt, accel, sig),
                }),
              ]),
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.columnForce,
          `${strings.columnSize} (${unitSymbol(FORCE, unit)})`,
          `${strings.columnAngle} (°)`,
          `${strings.columnFx} (${unitSymbol(FORCE, unit)})`,
          `${strings.columnFy} (${unitSymbol(FORCE, unit)})`,
        ]}
        rows={[
          ...forces.map((force, index) => [
            t(strings.forceLabel, { n: index + 1 }),
            formatSig(fmt, fromBase(force.magnitude, FORCE, unit), sig),
            formatSig(fmt, force.angle * RAD_TO_DEG, sig),
            formatSig(
              fmt,
              fromBase(net.components[index].fx, FORCE, unit),
              sig
            ),
            formatSig(
              fmt,
              fromBase(net.components[index].fy, FORCE, unit),
              sig
            ),
          ]),
          [
            strings.netForce,
            formatSig(fmt, fromBase(net.magnitude, FORCE, unit), sig),
            net.angle === null ? "—" : formatSig(fmt, degrees, sig),
            formatSig(fmt, fromBase(net.fx, FORCE, unit), sig),
            formatSig(fmt, fromBase(net.fy, FORCE, unit), sig),
          ],
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.diagramHeading}</h3>
      <FreeBodyDiagram
        forces={forces.map((force, index) => ({
          ...force,
          label: t(strings.forceLabel, { n: index + 1 }),
        }))}
        labels={{ title: strings.diagramTitle, net: strings.diagramNet }}
        net={{ magnitude: net.magnitude, angle: net.angle }}
      />
      <p className={HINT}>{strings.diagramNote}</p>
    </>
  );
}
